import { useMemo } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

interface Feature {
  title: string;
  description: string;
  image: string;
}

export const FertigationSystems: React.FC = () => {
  const { t } = useTranslation();

  const features: Feature[] = useMemo(
    () => [
      {
        title: t("fertigation_feature_1_title"),
        description: t("fertigation_feature_1_desc"),
        image: "/auto.webp",
      },
      {
        title: t("fertigation_feature_2_title"),
        description: t("fertigation_feature_2_desc"),
        image: "/polycarbonate-main.webp",
      },
      {
        title: t("fertigation_feature_3_title"),
        description: t("fertigation_feature_3_desc"),
        image: "/Co2.webp",
      },
      {
        title: t("fertigation_feature_4_title"),
        description: t("fertigation_feature_4_desc"),
        image: "/hero3.webp",
      },
      {
        title: t("fertigation_feature_5_title"),
        description: t("fertigation_feature_5_desc"),
        image: "/Nutrient.webp",
      },
      {
        title: t("fertigation_feature_6_title"),
        description: t("fertigation_feature_6_desc"),
        image: "/polycarbonate3.jpg",
      },
      {
        title: t("fertigation_feature_7_title"),
        description: t("fertigation_feature_7_desc"),
        image: "/box-slider4.webp",
      },
      {
        title: t("fertigation_feature_8_title"),
        description: t("fertigation_feature_8_desc"),
        image: "/weather.webp",
      },
      {
        title: t("fertigation_feature_9_title"),
        description: t("fertigation_feature_9_desc"),
        image: "/remote.webp",
      },
      {
        title: t("fertigation_feature_10_title"),
        description: t("fertigation_feature_10_desc"),
        image: "/resource.webp",
      },
      {
        title: t("fertigation_feature_11_title"),
        description: t("fertigation_feature_11_desc"),
        image: "/uniform.webp",
      },
      {
        title: t("fertigation_feature_12_title"),
        description: t("fertigation_feature_12_desc"),
        image: "/crop.webp",
      },
      
    ],
    []
  );

  return (
    <>
      {/* ================= HERO ================= */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
        className="relative w-full h-[55vh] sm:h-[65vh] md:h-[70vh] flex items-center justify-center overflow-hidden"
      >
        <img
          src="/automation-hero.jpg"
          alt="Climate Control & Fertigation"
          className="absolute inset-0 w-full h-full object-cover"
        />

        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-black/70" />

        <div className="relative z-10 text-center px-4 sm:px-6 max-w-4xl">
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold text-white leading-tight">
            {t("fertigation_hero_part1")}
            <span className="block">&</span>
            <span className="text-[#7FD655] block mt-2">
              {t("fertigation_hero_part2")}
            </span>
          </h1>
        </div>
      </motion.section>

      {/* ================= FEATURES ================= */}
      <section className="py-12 sm:py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#1C4734] text-center mb-12 sm:mb-16">
            {t("fertigation_features_heading")}
          </h2>

          <div className="space-y-14 sm:space-y-20">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                viewport={{ once: true }}
                className={`flex flex-col ${index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                  } items-center gap-8 md:gap-14`}
              >
                {/* IMAGE */}
                <div className="w-full md:w-1/2 overflow-hidden rounded-2xl shadow-lg">
                  <img
                    src={feature.image}
                    alt={feature.title}
                    className="w-full h-64 sm:h-72 md:h-96 object-cover"
                  />
                </div>

                {/* TEXT */}
                <div className="w-full md:w-1/2 bg-white p-6 sm:p-8 md:p-10 rounded-2xl shadow-md border border-gray-100">
                  <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1C4734] mb-4">
                    {feature.title}
                  </h3>

                  <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-6">
                    {feature.description}
                  </p>

                  <div className="flex flex-wrap gap-2 sm:gap-3">
                    <span className="px-3 py-1.5 bg-[#7FD655]/10 text-[#1C4734] text-xs sm:text-sm rounded-full border border-[#7FD655]/20">
                      Real-time Monitoring
                    </span>
                    <span className="px-3 py-1.5 bg-[#7FD655]/10 text-[#1C4734] text-xs sm:text-sm rounded-full border border-[#7FD655]/20">
                      AI-Powered
                    </span>
                    <span className="px-3 py-1.5 bg-[#7FD655]/10 text-[#1C4734] text-xs sm:text-sm rounded-full border border-[#7FD655]/20">
                      Auto-Adaptive
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= CTA ================= */}
     
    </>
  );
};
